import httpClient from "../http/http-client";
import { IReadiness } from './readiness.model';

// The readiness evaluation is served from a short-lived module cache so that walking
// dashboard, reporting and an entity page does not run the same evaluation three times in
// quick succession (each surface constructs its own service instance, so the cache lives at
// module level). The TTL is short enough that "checks run continuously against your data"
// stays true to the eye, and any mutation through this service busts the cache so the
// readiness page always re-reads its own writes.
const CACHE_TTL_MS = 20_000;
const readinessCache = new Map<string, { at: number, request: Promise<IReadiness> }>();

const bustReadinessCache = () => readinessCache.clear();

export default class ReadinessService {
  http;

  constructor() {
    this.http = httpClient
  }

  async getForPeriod(reportPeriodId: string): Promise<IReadiness> {
    const cached = readinessCache.get(reportPeriodId);
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
      return cached.request
    }
    const request = this.http.get(`readiness/${reportPeriodId}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        // A failed evaluation is never cached: the next surface retries rather than
        // inheriting the failure for the rest of the TTL.
        readinessCache.delete(reportPeriodId)
        console.log(err);
        throw err;
      })
    readinessCache.set(reportPeriodId, { at: Date.now(), request })
    return request
  }

  async acknowledgeFinding(reportPeriodId: string, payload: { checkId: string, entityKey: string, reason: string, expiresAt?: string | null }): Promise<{ id: string }> {
    return this.http.post(`readiness/${reportPeriodId}/acknowledge`, payload)
      .then(response => {
        bustReadinessCache()
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      })
  }

  async createTaskFromFinding(reportPeriodId: string, payload: { checkId: string, entityKey: string, userId?: string | null }): Promise<{ id: string }> {
    return this.http.post(`readiness/${reportPeriodId}/create-task`, payload)
      .then(response => {
        bustReadinessCache()
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      })
  }

  async releaseAcknowledgement(acknowledgementId: string): Promise<void> {
    return this.http.post(`readiness/acknowledgement/${acknowledgementId}/release`)
      .then(response => {
        bustReadinessCache()
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      })
  }
}
